import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { ChevronDown, Building2, Sparkles, ArrowLeftRight, Loader2 } from "lucide-react";

/**
 * Workspace Selector — top-bar dropdown that lets the authenticated
 * customer flip between:
 *   - "My VAULT" (their real workspace)
 *   - "VAULT Demo" (the shared read-only demo tenant)
 *
 * Uses:
 *   GET  /api/workspace/current   — current state
 *   GET  /api/my-tenant           — home workspace name
 *   POST /api/workspace/switch    — pivot into demo
 *   POST /api/workspace/return    — pivot back
 *
 * On success the page reloads so every open module re-fetches from the
 * new tenant.
 */
export default function WorkspaceSelector() {
    const [open, setOpen] = useState(false);
    const [state, setState] = useState(null);        // /api/workspace/current
    const [homeName, setHomeName] = useState("My VAULT");
    const [busy, setBusy] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        let cancelled = false;
        api.get("/workspace/current").then((r) => { if (!cancelled) setState(r.data); }).catch(() => {});
        api.get("/my-tenant").then((r) => {
            if (cancelled) return;
            const t = r.data?.tenant;
            if (t?.id === "global-demo") return; // display is driven by state
            setHomeName(t?.name || "My VAULT");
        }).catch(() => {});
        const onDocClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => { cancelled = true; document.removeEventListener("mousedown", onDocClick); };
    }, []);

    const isDemo = state?.is_demo;
    const label = isDemo ? "VAULT Demo" : homeName;

    const enterDemo = async () => {
        if (busy || isDemo) return;
        setBusy(true);
        try {
            await api.post("/workspace/switch");
            window.location.reload();
        } catch { setBusy(false); }
    };

    const exitDemo = async () => {
        if (busy || !isDemo) return;
        setBusy(true);
        try {
            await api.post("/workspace/return");
            window.location.reload();
        } catch { setBusy(false); }
    };

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen((s) => !s)}
                data-testid="workspace-selector-btn"
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm max-w-[220px] transition-colors ${
                    isDemo
                        ? "border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100"
                        : "border-stone-200 text-stone-700 hover:border-stone-300 bg-white"
                }`}
            >
                {isDemo ? <Sparkles size={13} /> : <Building2 size={13} />}
                <span className="truncate max-w-[140px]">{label}</span>
                {isDemo && (
                    <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-500 text-white">
                        Demo
                    </span>
                )}
                <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div
                    data-testid="workspace-selector-menu"
                    className="absolute right-0 mt-2 w-72 rounded-2xl border border-stone-200 bg-white shadow-xl overflow-hidden z-50"
                >
                    <div className="px-4 pt-3 pb-2 text-[10px] font-bold uppercase tracking-widest text-stone-500 border-b border-stone-100">
                        Switch workspace
                    </div>
                    {/* My VAULT */}
                    <button
                        onClick={exitDemo}
                        disabled={!isDemo || busy}
                        data-testid="workspace-selector-mine"
                        className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-stone-50 transition-colors ${
                            !isDemo ? "bg-stone-50" : ""
                        }`}
                    >
                        <span className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center shrink-0">
                            <Building2 size={14} className="text-stone-700" />
                        </span>
                        <span className="flex-1 min-w-0">
                            <span className="block text-[13px] font-semibold text-stone-900 truncate">{homeName}</span>
                            <span className="block text-[11px] text-stone-500">Your live data · fully editable</span>
                        </span>
                        {!isDemo && <span className="text-[10px] font-bold text-emerald-700">CURRENT</span>}
                    </button>
                    {/* VAULT Demo */}
                    <button
                        onClick={enterDemo}
                        disabled={isDemo || busy}
                        data-testid="workspace-selector-demo"
                        className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-amber-50 transition-colors border-t border-stone-100 ${
                            isDemo ? "bg-amber-50" : ""
                        }`}
                    >
                        <span className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                            <Sparkles size={14} className="text-amber-700" />
                        </span>
                        <span className="flex-1 min-w-0">
                            <span className="block text-[13px] font-semibold text-stone-900 truncate flex items-center gap-1.5">
                                VAULT Demo
                                <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-500 text-white">Demo</span>
                            </span>
                            <span className="block text-[11px] text-stone-500">Sample data · read-only · safe to explore</span>
                        </span>
                        {isDemo && <span className="text-[10px] font-bold text-amber-800">CURRENT</span>}
                        {busy && <Loader2 size={12} className="animate-spin text-stone-400" />}
                    </button>
                    <div className="px-4 py-2 border-t border-stone-100 text-[10.5px] text-stone-500 flex items-center gap-1.5">
                        <ArrowLeftRight size={11} />
                        Changes in Demo are never saved
                    </div>
                </div>
            )}
        </div>
    );
}
